import { useTranslation } from "react-i18next";

import { LicenseStatistics as Statistics } from "@admin/api/license";
import { Box, CircularProgress, Typography } from "@mui/material";
import { SecondaryButton } from "@shared/CustomButton";

import { useStyles } from "./AdminLicensesPage.style";

type Props = {
    statistics: Statistics | null;
    isLoading: boolean;
    isError: boolean;
    onRetry: () => void;
};

export const LicenseStatistics = ({ statistics, isLoading, isError, onRetry }: Props) => {
    const { t } = useTranslation(["common", "pages"]);
    const { classes, cx } = useStyles();

    if (isError) {
        return (
            <Box className={classes.statisticsStatus}>
                <Typography color="error">{t("common:error.technicalDifficulties.message")}</Typography>
                <SecondaryButton onClick={onRetry}>{t("common:error.technicalDifficulties.refreshButton")}</SecondaryButton>
            </Box>
        );
    }

    const rows = [
        {
            key: "total",
            label: t("pages:adminLicensesPage.statistics.total"),
            value: statistics?.totalLicenses,
            dotClass: classes.neutralDot,
        },
        {
            key: "activated",
            label: t("pages:adminLicensesPage.statistics.activated"),
            value: statistics?.totalAssociatedLicenses,
            dotClass: classes.successDot,
        },
        {
            key: "revoked",
            label: t("pages:adminLicensesPage.statistics.revoked"),
            value: statistics?.totalRevokedLicenses,
            dotClass: classes.errorDot,
        },
    ];

    return (
        <Box className={classes.statisticsRow}>
            {rows.map((row) => (
                <Box key={row.key}>
                    <Box className={classes.statisticsLabel}>
                        <Box className={cx(classes.statusDot, row.dotClass)} />
                        <Typography variant="caption">{row.label}</Typography>
                    </Box>
                    <Typography variant="h5" fontWeight={600} component="div">
                        {isLoading ? <CircularProgress size={20} /> : (row.value ?? "-")}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
};
